import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { CreateScholarship, Scholarship, ScholarshipApplicationWindow, UpdateScholarship } from '../models/scholarship';
import { CacheService } from '../services/cache.service';
import { ApiService } from './base-api/api.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ScholarshipApiService {
  private readonly api = inject(ApiService);
  private readonly cache = inject(CacheService);

  /**
   * Lists scholarships.
   *
   * Pass `acceptingOnly` for the public catalogue: the API then leaves out the
   * scholarships whose applications are closed (closing date passed, or closed
   * by an administrator). The admin console uses the unfiltered list so it can
   * still manage them.
   */
  getScholarships(acceptingOnly = false): Observable<Scholarship[]> {
    const key = acceptingOnly ? 'scholarships-accepting' : 'scholarships';
    const url = acceptingOnly ? 'scholarships?acceptingOnly=true' : 'scholarships';

    return this.cache.wrap(
      key,
      this.api.get<{ data: Scholarship[] }>(url).pipe(
        map(response => response.data)
      ),
      environment.cacheTimeout
    );
  }

  getScholarshipById(id: number): Observable<Scholarship> {
    return this.getScholarships().pipe(
      map(scholarships => {
        const scholarship = scholarships.find(s => s.id == id);
        if (!scholarship) {
          throw new Error(`Scholarship with id ${id} not found`);
        }
        return scholarship;
      })
    );
  }

  saveScholarship(scholarship: CreateScholarship): Observable<string> {
    return this.api.post('scholarships', scholarship);
  }

  updateScholarship(scholarship: UpdateScholarship): Observable<string> {
    return this.api.put(`scholarships/${scholarship.id}`, scholarship);
  }

  /**
   * Overrides the application window: FORCE_CLOSED closes the applications now,
   * FORCE_OPEN re-opens them even after the closing date and AUTOMATIC hands
   * control back to the closing date.
   */
  setApplicationWindow(id: number, applicationWindow: ScholarshipApplicationWindow): Observable<void> {
    return this.api.put<void>(`scholarships/${id}/application-window`, { applicationWindow });
  }

  deleteScholarship(id: number): Observable<void> {
    return this.api.delete<void>(`scholarships/${id}`);
  }

  invalidateCache(): void {
    this.cache.invalidate('scholarships');
    this.cache.invalidate('scholarships-accepting');
  }
}